import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import CustomModal from "./customModal";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import showErrorToast from "../toast/toast";

export default function IngredientSelectionModal({
  isOpen,
  onClose,
  onSelectIngredients,
  compID,
  existingIngredients = [], // Array of already selected ingredient IDs
}) {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(new Set());

  const fetchProducts = useCallback(async () => {
    if (!compID || !isOpen) return;

    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_ONLY_PRODUCTS, {
        params: { CompID: compID },
      });

      const productsData = response.data.data || response.data;
      if (Array.isArray(productsData)) {
        // Filter out null/undefined values and ensure menuItem_ID exists
        const validProducts = productsData.filter(
          (product) => product != null && product.menuItem_ID != null
        );
        setProducts(validProducts);
        setFilteredProducts(validProducts);
      } else {
        setProducts([]);
        setFilteredProducts([]);
      }
    } catch (err) {
      setProducts([]);
      setFilteredProducts([]);
      showErrorToast("Failed to fetch products");
    } finally {
      setLoading(false);
    }
  }, [compID, isOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchProducts();
      // Initialize selected products from existing ingredients
      const existingIds = new Set(
        existingIngredients.map((ing) => ing.ingredientID || ing.menuItem_ID).filter(Boolean)
      );
      setSelectedProducts(existingIds);
    } else {
      setSearchTerm("");
      setSelectedProducts(new Set());
    }
  }, [isOpen, fetchProducts, existingIngredients]);

  // Client-side filtering
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(
        (product) =>
          product != null &&
          product.menuItem_ID != null &&
          (product.menuItem_Name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            product.menuItem_SKU?.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  const handleProductToggle = (productId) => {
    setSelectedProducts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(productId)) {
        newSet.delete(productId);
      } else {
        newSet.add(productId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allIds = new Set(filteredProducts.map((p) => p.menuItem_ID).filter(Boolean));
    setSelectedProducts(allIds);
  };

  const handleDeselectAll = () => {
    setSelectedProducts(new Set());
  };

  const handleContinue = () => {
    const selectedItems = products.filter((p) =>
      selectedProducts.has(p.menuItem_ID)
    );
    onSelectIngredients(selectedItems);
    onClose();
  };

  return (
    <CustomModal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Ingredients"
      subtitle="Choose products to add as ingredients (multi-select)"
      onSubmit={handleContinue}
      showButton={true}
      showSubmitButton={true}
      showCancelButton={true}
      submitButtonText={`Add`}
      submitButtonDisabled={selectedProducts.size === 0}
      modalSize="modal-lg"
      maxHeight="600px"
      isCentered={true}
    >
      <div>
        {/* Search Bar */}
        <div className="mb-3">
          <div
            style={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: "16px",
                zIndex: 2,
                color: isDarkMode ? "#ffffff" : "#6c757d",
                fontSize: "16px",
              }}
            >
              <i className="fa fa-search"></i>
            </div>
            <input
              type="text"
              className="form-control"
              placeholder="Search products..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                paddingLeft: "45px",
                backgroundColor: isDarkMode ? "#1e1e1e" : "#ffffff",
                border: `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
                color: isDarkMode ? "#ffffff" : "#000000",
                borderRadius: "8px",
              }}
            />
          </div>
        </div>

        {/* Select All / Deselect All */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <span style={{ color: isDarkMode ? "#adb5bd" : "#6c757d", fontSize: "14px" }}>
              {selectedProducts.size} selected
            </span>
          </div>
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm"
              onClick={handleSelectAll}
              style={{
                backgroundColor: "transparent",
                border: `1px solid ${themeColor}`,
                color: themeColor,
                fontSize: "12px",
                padding: "4px 12px",
              }}
            >
              Select All
            </button>
            <button
              type="button"
              className="btn btn-sm"
              onClick={handleDeselectAll}
              style={{
                backgroundColor: "transparent",
                border: `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
                color: isDarkMode ? "#ffffff" : "#000000",
                fontSize: "12px",
                padding: "4px 12px",
              }}
            >
              Deselect All
            </button>
          </div>
        </div>

        {/* Products List */}
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-5">
            <p style={{ color: isDarkMode ? "#adb5bd" : "#6c757d" }}>
              No products found
            </p>
          </div>
        ) : (
          <div
            style={{
              maxHeight: "400px",
              overflowY: "auto",
            }}
          >
            {filteredProducts
              .filter((product) => product != null && product.menuItem_ID != null)
              .map((product) => {
                const isSelected = selectedProducts.has(product.menuItem_ID);
                return (
                  <div
                    key={product.menuItem_ID}
                    onClick={() => handleProductToggle(product.menuItem_ID)}
                    style={{
                      padding: "12px",
                      marginBottom: "8px",
                      borderRadius: "8px",
                      cursor: "pointer",
                      backgroundColor: isSelected
                        ? `${themeColor}20`
                        : isDarkMode
                        ? "#1e1e1e"
                        : "#f8f9fa",
                      border: isSelected
                        ? `2px solid ${themeColor}`
                        : `1px solid ${isDarkMode ? "#404040" : "#dee2e6"}`,
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = isDarkMode
                          ? "#2a2a2a"
                          : "#e9ecef";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.currentTarget.style.backgroundColor = isDarkMode
                          ? "#1e1e1e"
                          : "#f8f9fa";
                      }
                    }}
                  >
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="d-flex align-items-center gap-3">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleProductToggle(product.menuItem_ID)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: "18px",
                            height: "18px",
                            cursor: "pointer",
                          }}
                        />
                        <div>
                          <h6
                            style={{
                              margin: 0,
                              color: isDarkMode ? "#ffffff" : "#000000",
                              fontWeight: "600",
                            }}
                          >
                            {product.menuItem_Name}
                          </h6>
                          {product.menuItem_SKU && (
                            <small
                              style={{
                                color: isDarkMode ? "#adb5bd" : "#6c757d",
                              }}
                            >
                              SKU: {product.menuItem_SKU}
                            </small>
                          )}
                          {product.rootCat_Name && (
                            <small
                              style={{
                                color: isDarkMode ? "#adb5bd" : "#6c757d",
                                display: "block",
                              }}
                            >
                              Category: {product.rootCat_Name}
                            </small>
                          )}
                        </div>
                      </div>
                      {isSelected && (
                        <i
                          className="fa fa-check-circle"
                          style={{
                            color: themeColor,
                            fontSize: "20px",
                          }}
                        ></i>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </CustomModal>
  );
}

